(ns graph-ui.web.islands
  "Every island this deployment holds, for the top bar's island picker — the
   `island-picker` part of `ikc/top-bar` (dsl/platform/top-bar.edn).

   IT NEEDS NO NEW COMMAND, which is why it can ship without a bus restart.
   `:ui-islands` is an existing source and its own declaration says it is *not*
   session-scoped: \"the set of islands is the same for everyone\". So one
   `:ui/read-many` fills the whole menu, over a wire that already carries it.

   THE REPLY IS POSITIONAL, and that is the one trap here. `:ui/read-many`
   answers with a VECTOR of values, one per requested read, in the order asked —
   not a map keyed by source name. Reading it as a map returns nil, and nil
   counts as an empty list, so the menu comes back empty and blames the server.
   Measured against the live bus on 2026-09-02: one read, 14 islands, and
   `(:ui-islands reply)` was nil while `(first reply)` was the list.

   ONE ENTRY IS `{:value <island-id-string> :label <title>}`, which is the shape
   `:ui-islands` returns. The picker links by TITLE, because `?island=` takes a
   title (`core/island-from-url`). The id is carried anyway: it is the stable
   half, and a title with punctuation in it is a link waiting to break."
  (:require [reagent.core :as r]
            [graph-ui.web.bind :as bind]))

;; @feature F-graph-ui-v0-014-01-island-picker
;; @adr ADR-graph-ui-v0-014-01-ui-building-blocks

(defonce ^:private listing (r/atom []))

;; Islands that are NOT places a reader goes. They stay in the menu, because
;; hiding them would make the picker disagree with the graph, but they sort to
;; the bottom so the twelve real ones read as the list.
;;
;; A PREFIX AND A SET, not a hand-kept list of every island: `sketch:` is a
;; naming convention the corpus already uses, and the set holds the fixtures
;; that carry no such mark. A new sketch needs no edit here.
(def ^:private secondary-titles #{"kinds-acceptance"})
(def ^:private secondary-prefix "sketch")

(defn secondary?
  "PURE. Is this island a sketch or a test fixture rather than a place to work?"
  [title]
  (let [t (str title)]
    (or (contains? secondary-titles t)
        (.startsWith t secondary-prefix))))

(defn order
  "PURE. The menu order: real islands first, then sketches and fixtures, each
   group by title. `:ui-islands` already sorts by title, but it cannot know
   which of them a reader actually wants."
  [entries]
  (vec (sort-by (juxt #(if (secondary? (:label %)) 1 0) #(str (:label %))) entries)))

(defn href
  "PURE. Where a menu row points. Encoded, because one island is titled
   `sketch: alatus/graph-view` — a colon, a space and a slash, all of which
   change the meaning of a query string."
  [title]
  (str "?island=" (js/encodeURIComponent (str title))))

(defn current?
  "PURE. Is this row the island already on the screen? Its plain click is a
   NOOP — a reload to arrive where you already are costs a second and loses the
   session's place (George, 2026-09-02). Opening it in a NEW tab is not a noop
   and stays available: two windows onto one island is a thing to want."
  [title current-title]
  (= (str title) (str current-title)))

(defn refresh!
  "Read `:ui-islands` and keep it. Safe to call again — it replaces the list."
  [bus]
  (when bus
    (js-invoke bus "dispatch"
               {:type :ui/read-many :reads [{:source :ui-islands}]}
               (fn [result]
                 ;; An error reply is not data: keep whatever the menu had
                 ;; rather than empty it, so a refused read shows a stale list
                 ;; instead of claiming this deployment has no islands.
                 (when-not (bind/reply-failed? result)
                   (let [v (first result)]
                     (when (and (sequential? v) (seq v))
                       (reset! listing (order (vec v))))))))))

(defn entries
  "Reactive read of the island list, in menu order. Empty until the first
   `refresh!` replies, which is why the trigger draws the island's own name
   rather than waiting for this."
  []
  @listing)

(defn clear!
  "For the tests."
  []
  (reset! listing []))
